// Airport detail values are independent of live mode/HMS. Only explicitly received fields refresh their clocks.
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const number = value => value === null || value === undefined || value === '' || typeof value === 'boolean' || !Number.isFinite(Number(value)) ? undefined : Number(value)
export const DOCK_DETAIL_PATHS = [
  'acc_time', 'job_number', 'activation_time', 'working_voltage', 'working_current',
  'temperature', 'humidity', 'environment_temperature', 'rainfall', 'wind_speed',
  'supplement_light_state', 'cover_state', 'drone_in_dock', 'drc_state',
  'network_state.type', 'network_state.quality', 'network_state.rate',
  'position_state.is_calibration', 'position_state.rtk_number',
  'backup_battery.switch', 'backup_battery.voltage', 'backup_battery.temperature',
  'air_conditioner.air_conditioner_state', 'alternate_land_point.is_configured',
  'alternate_land_point.longitude', 'alternate_land_point.latitude', 'alternate_land_point.safe_land_height', 'alternate_land_point.height'
]
const workKeys = new Set(['acc_time', 'job_number', 'activation_time', 'working_voltage', 'working_current', 'backup_battery', 'electric_supply_voltage', 'maintain_status', 'drone_battery_maintenance_info'])
const linkKeys = new Set(['media_file_detail', 'drc_state', 'wireless_link', 'drc_current_client_id', 'drc_current_client_name'])
function get (data, path) { return path.split('.').reduce((value, key) => value?.[key], data) }
function set (data, path, value) {
  const parts = path.split('.'); const last = parts.pop()
  let target = data
  for (const key of parts) target = target[key] ||= {}
  target[last] = value
}
function mergeObject (previous, report) {
  const out = { ...previous }
  for (const [key, value] of Object.entries(report || {})) {
    if (value === undefined || value === null) continue
    out[key] = object(value) ? mergeObject(object(out[key]) ? out[key] : {}, value) : value
  }
  return out
}
function leafTimes (data, times, clocks, receivedAt, path = '') {
  for (const [key, value] of Object.entries(data || {})) {
    if (key.startsWith('_detail_') || key === 'detail_field_times' || value === null || value === undefined) continue
    const field = path ? `${path}.${key}` : key
    if (object(value)) leafTimes(value, times, clocks, receivedAt, field)
    else times[field] = clocks?.[field] ?? receivedAt
  }
}
export function mergeDockReport (previous = {}, report = {}, receivedAt = Date.now()) {
  const result = { ...previous, _detail_field_times: { ...previous._detail_field_times } }
  const basic = {}; const work = {}; const link = {}
  for (const [key, value] of Object.entries(report)) {
    if (key.startsWith('_detail_') || key === 'detail_field_times' || value === null || value === undefined) continue
    ;(workKeys.has(key) ? work : linkKeys.has(key) ? link : basic)[key] = value
    // Existing control pages read DRC ownership from basic_osd; retain that contract.
    if (key === 'drc_state' || key.startsWith('drc_current_client_')) basic[key] = value
  }
  result.basic_osd = mergeObject(previous.basic_osd, basic)
  result.work_osd = mergeObject(previous.work_osd, work)
  result.link_osd = mergeObject(previous.link_osd, link)
  leafTimes(report, result._detail_field_times, report.detail_field_times, receivedAt)
  if (report.mode_code !== undefined && report.mode_code !== null) result._detail_basic_at = receivedAt
  if (report.battery_store_mode !== undefined && report.battery_store_mode !== null) result._detail_battery_mode_at = report.battery_store_mode_reported_at ?? receivedAt
  if (object(report.wireless_link)) {
    const paired = report.wireless_link_aircraft_sn ?? report.sub_device?.device_sn ?? result.basic_osd?.sub_device?.device_sn
    result.link_osd.wireless_link = mergeObject(paired && paired === previous._detail_link_aircraft_sn ? previous.link_osd?.wireless_link : {}, report.wireless_link)
    result._detail_wireless_times = paired && paired === previous._detail_link_aircraft_sn ? { ...previous._detail_wireless_times } : {}
    for (const [key, value] of Object.entries(report.wireless_link)) if (value !== undefined && value !== null) result._detail_wireless_times[key] = report.detail_field_times?.[`wireless_link.${key}`] ?? report.wireless_link_reported_at ?? receivedAt
    result._detail_link_at = report.wireless_link_reported_at ?? receivedAt
    result._detail_link_aircraft_sn = paired
  }
  return result
}
export function resolveDockDetail ({ live = {}, saved = {}, dockSn }) {
  const values = {}; const samples = {}
  const flat = { ...live.basic_osd, ...live.work_osd, ...live.link_osd }
  for (const path of DOCK_DETAIL_PATHS) {
    const stored = saved?.[path]
    let sample = stored?.source_sn === dockSn ? { ...stored, kind: 'saved' } : undefined
    const value = get(flat, path); const at = live._detail_field_times?.[path] || 0
    if (value !== undefined && value !== null && value !== '' && (!sample || at >= (sample.reported_at || 0))) sample = { value, reported_at: at, source_sn: dockSn, kind: 'dock' }
    if (sample && sample.value !== null && sample.value !== undefined && sample.value !== '') { set(values, path, sample.value); samples[path] = sample }
  }
  // An unconfigured point has no usable coordinates. Never substitute the dock's root height.
  if ([false, 0].includes(values.alternate_land_point?.is_configured)) {
    for (const key of ['longitude', 'latitude', 'safe_land_height', 'height']) delete values.alternate_land_point[key]
  }
  return { values, samples }
}
export function compactDockDuration (value) {
  const seconds = number(value)
  if (seconds === undefined || seconds < 0) return '-'
  if (seconds < 60) return `${Math.floor(seconds)}秒`
  let remaining = Math.floor(seconds)
  const years = Math.floor(remaining / 31536000); remaining %= 31536000
  const months = Math.floor(remaining / 2592000); remaining %= 2592000
  const days = Math.floor(remaining / 86400); remaining %= 86400
  const hours = Math.floor(remaining / 3600); remaining %= 3600
  const minutes = Math.floor(remaining / 60)
  if (years) return `${years}年${months}月${days}天`
  if (months) return `${months}月${days}天${hours}时`
  if (days) return `${days}天${hours}时${minutes}分`
  return `${hours}时${minutes}分`
}
export function activationTime (value) {
  const seconds = number(value)
  if (seconds === undefined || seconds <= 0) return '-'
  const date = new Date(seconds * 1000)
  if (!Number.isFinite(date.getTime())) return '-'
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date)
}
function enumText (value, options) { const n = number(value); return n === undefined ? '-' : options[n] ?? `未知（${n}）` }
export const networkType = value => enumText(value, { 1: '4G', 2: '以太网' })
export const networkQuality = value => enumText(value, { 0: '无信号', 1: '差', 2: '较差', 3: '一般', 4: '较好', 5: '好' })
